import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Sparkles, X, Check, Copy, RefreshCw, AlertCircle, Hash } from 'lucide-react';

interface CaptionVariant {
  style: string;
  caption: string;
}

interface HashtagTiers {
  broad: string[];
  mid: string[];
  niche: string[];
}

interface AIResult {
  captions: CaptionVariant[];
  hashtags: HashtagTiers;
}

interface Props {
  open: boolean;
  onClose: () => void;
  theme: string;
  imageUrl: string;
  dayLabel: string;
  postType: string;
  existingCaption: string;
  onInsertCaption: (caption: string) => void;
  onInsertHashtags: (tags: string[]) => void;
}

const styleAccent: Record<string, string> = {
  Cinematic: 'border-l-[#1A3B5C]',
  Conversational: 'border-l-[#C9A961]',
  Editorial: 'border-l-emerald-600',
};

const CaptionSuggestModal: React.FC<Props> = ({
  open,
  onClose,
  theme,
  imageUrl,
  dayLabel,
  postType,
  existingCaption,
  onInsertCaption,
  onInsertHashtags,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AIResult | null>(null);
  const [selectedTiers, setSelectedTiers] = useState<{ broad: boolean; mid: boolean; niche: boolean }>({
    broad: true,
    mid: true,
    niche: true,
  });
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('caption-suggest', {
        body: { theme, imageUrl, dayLabel, postType, existingCaption },
      });
      if (invokeErr) throw new Error(invokeErr.message);
      if (!data || data.error) throw new Error(data?.error || 'No result returned');
      setResult(data as AIResult);
    } catch (e: any) {
      setError(e?.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate when modal opens
  useEffect(() => {
    if (open && !result && !loading) {
      generate();
    }
    if (!open) {
      // reset when closing so next open is fresh
      setResult(null);
      setError(null);
      setCopiedIdx(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const selectedTags = result
    ? [
        ...(selectedTiers.broad ? result.hashtags.broad : []),
        ...(selectedTiers.mid ? result.hashtags.mid : []),
        ...(selectedTiers.niche ? result.hashtags.niche : []),
      ]
    : [];

  const handleInsertCaption = (caption: string, idx: number) => {
    onInsertCaption(caption);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const handleInsertHashtags = () => {
    onInsertHashtags(selectedTags);
    onClose();
  };

  const handleCopy = async (text: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0D1B2A]/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-4xl my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-[#0D1B2A] to-[#1A3B5C]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C9A961]" />
            </div>
            <div>
              <h2 className="font-serif text-white text-lg font-light">AI Caption Studio</h2>
              <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/50">
                {dayLabel} · {theme || 'Untitled theme'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white p-2 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Context row */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-4 flex-wrap">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              className="w-12 h-12 object-cover border border-slate-200"
            />
          ) : (
            <div className="w-12 h-12 bg-slate-200 flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-slate-400" />
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <div className="font-sans text-[10px] tracking-wider uppercase text-slate-500">Generating from</div>
            <div className="font-sans text-xs text-slate-700">
              {imageUrl ? 'Image vision + theme + brand voice' : 'Theme + brand voice (no image attached)'}
            </div>
          </div>
          <button
            onClick={generate}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A961]/40 bg-[#C9A961]/10 text-[#8B6F47] font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Generating...' : 'Regenerate'}
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
          {loading && !result && (
            <div className="py-20 text-center">
              <div className="inline-flex items-center gap-3 font-sans text-sm text-slate-500">
                <Sparkles className="w-4 h-4 text-[#C9A961] animate-pulse" />
                Reading the image, writing in Zera's voice...
              </div>
              <p className="font-sans text-xs text-slate-400 mt-2">Usually 6–10 seconds.</p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 font-sans text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold mb-1">Generation failed</div>
                <div className="text-xs">{error}</div>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-8">
              {/* CAPTIONS */}
              <section>
                <h3 className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#1A3B5C] font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-[#C9A961]" /> Caption Variants
                  <span className="font-sans text-[10px] tracking-wider uppercase text-slate-400 ml-auto">
                    Click any to insert
                  </span>
                </h3>
                <div className="space-y-3">
                  {result.captions.map((v, idx) => (
                    <div
                      key={idx}
                      className={`group bg-white border border-slate-200 border-l-4 ${
                        styleAccent[v.style] || 'border-l-slate-400'
                      } p-4 hover:shadow-md transition-shadow`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B6F47] font-semibold">
                          {v.style}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleCopy(v.caption, idx)}
                            className="px-2 py-1 text-slate-400 hover:text-[#1A3B5C] font-sans text-[10px] tracking-wider uppercase flex items-center gap-1"
                            title="Copy"
                          >
                            <Copy className="w-3 h-3" /> Copy
                          </button>
                          <button
                            onClick={() => handleInsertCaption(v.caption, idx)}
                            className="px-3 py-1.5 bg-[#1A3B5C] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors flex items-center gap-1"
                          >
                            {copiedIdx === idx ? (
                              <><Check className="w-3 h-3" /> Inserted</>
                            ) : (
                              <>Use This</>
                            )}
                          </button>
                        </div>
                      </div>
                      <p className="font-sans text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                        {v.caption}
                      </p>
                      <div className="font-sans text-[10px] text-slate-400 mt-2">
                        {v.caption.length} chars · {v.caption.split(/\s+/).filter(Boolean).length} words
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* HASHTAGS */}
              <section>
                <h3 className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#1A3B5C] font-semibold mb-3 flex items-center gap-2">
                  <Hash className="w-3.5 h-3.5 text-[#C9A961]" /> Hashtag Set · 30 tags by reach tier
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <TierCard
                    name="Broad"
                    subtitle="High-volume · discovery"
                    tags={result.hashtags.broad}
                    selected={selectedTiers.broad}
                    onToggle={() => setSelectedTiers((s) => ({ ...s, broad: !s.broad }))}
                    accent="bg-sky-50 border-sky-200 text-sky-700"
                  />
                  <TierCard
                    name="Mid"
                    subtitle="Regional · relevance"
                    tags={result.hashtags.mid}
                    selected={selectedTiers.mid}
                    onToggle={() => setSelectedTiers((s) => ({ ...s, mid: !s.mid }))}
                    accent="bg-amber-50 border-amber-200 text-amber-700"
                  />
                  <TierCard
                    name="Niche"
                    subtitle="Specific · community"
                    tags={result.hashtags.niche}
                    selected={selectedTiers.niche}
                    onToggle={() => setSelectedTiers((s) => ({ ...s, niche: !s.niche }))}
                    accent="bg-emerald-50 border-emerald-200 text-emerald-700"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-[#F5F1E8] border border-[#C9A961]/30">
                  <div>
                    <div className="font-sans text-[10px] tracking-wider uppercase text-[#8B6F47]">
                      {selectedTags.length} of 30 tags selected
                    </div>
                    <div className="font-sans text-xs text-slate-600 mt-1 max-w-md truncate">
                      {selectedTags.slice(0, 6).join(' ')}{selectedTags.length > 6 ? ' ...' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigator.clipboard.writeText(selectedTags.join(' '))}
                      className="px-3 py-2 border border-slate-300 text-slate-600 font-sans text-[10px] tracking-[0.2em] uppercase hover:border-[#C9A961] hover:text-[#C9A961] transition-colors flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button
                      onClick={handleInsertHashtags}
                      disabled={selectedTags.length === 0}
                      className="px-4 py-2 bg-[#1A3B5C] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Use Selected
                    </button>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ----- Subcomponent: TierCard -----

const TierCard: React.FC<{
  name: string;
  subtitle: string;
  tags: string[];
  selected: boolean;
  onToggle: () => void;
  accent: string;
}> = ({ name, subtitle, tags, selected, onToggle, accent }) => (
  <div
    className={`border ${
      selected ? 'border-[#1A3B5C] shadow-sm' : 'border-slate-200 opacity-60'
    } bg-white p-3 transition-all`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between mb-2 text-left"
    >
      <div>
        <div className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase font-semibold border ${accent}`}>
          {name}
        </div>
        <div className="font-sans text-[10px] text-slate-500 mt-1">{subtitle}</div>
      </div>
      <div
        className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 ${
          selected ? 'bg-[#1A3B5C] border-[#1A3B5C]' : 'bg-white border-slate-300'
        }`}
      >
        {selected && <Check className="w-3 h-3 text-white" />}
      </div>
    </button>
    <div className="flex flex-wrap gap-1">
      {tags.map((t, i) => (
        <span
          key={i}
          className="inline-block px-1.5 py-0.5 bg-slate-50 border border-slate-200 font-sans text-[10px] text-slate-600"
        >
          {t}
        </span>
      ))}
    </div>
    <div className="font-sans text-[10px] text-slate-400 mt-2">{tags.length} tags</div>
  </div>
);

export default CaptionSuggestModal;
